import { ChangeDetectionStrategy, Component, linkedSignal, output, signal, ViewEncapsulation } from "@angular/core";
import { disabled, email, FormField, form, required, submit } from "@angular/forms/signals";
import { FormsModule } from "@angular/forms";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { DBContact } from "../student-housing/student.housing.service";

export type FormPublicIdentityPayload = {
    firstName: string;
    familyName: string;
    email: string;
};

type FormPublicIdentityModel = {
    firstName: string;
    familyName: string;
    email: string;
};

@Component({
    selector: "s25-ng-form-public-identity",
    standalone: true,
    template: `
        <div class="identity-panel">
            <h2>Continue to form</h2>
            <p class="intro">Enter your name and email to continue.</p>
            <form (ngSubmit)="onSubmit()" (keydown.enter)="onSubmit()">
                <div class="field-grid">
                    @let firstNameField = identityForm.firstName();
                    <label class="field" [class.field--error]="showFieldErrors(firstNameField)">
                        <span class="field-label">First name <span class="required">*</span></span>
                        <input
                            class="cn-form__control"
                            type="text"
                            [formField]="identityForm.firstName"
                            autocomplete="given-name"
                        />
                        @if (showFieldErrors(firstNameField)) {
                            <span class="field-error">{{ firstNameField.errors()[0].message }}</span>
                        }
                    </label>

                    @let familyNameField = identityForm.familyName();
                    <label class="field" [class.field--error]="showFieldErrors(familyNameField)">
                        <span class="field-label">Last name <span class="required">*</span></span>
                        <input
                            class="cn-form__control"
                            type="text"
                            [formField]="identityForm.familyName"
                            autocomplete="family-name"
                        />
                        @if (showFieldErrors(familyNameField)) {
                            <span class="field-error">{{ familyNameField.errors()[0].message }}</span>
                        }
                    </label>

                    @let emailField = identityForm.email();
                    <label class="field field--full" [class.field--error]="showFieldErrors(emailField)">
                        <span class="field-label">Email <span class="required">*</span></span>
                        <input
                            class="cn-form__control"
                            type="email"
                            [formField]="identityForm.email"
                            autocomplete="email"
                        />
                        @if (showFieldErrors(emailField)) {
                            <span class="field-error">{{ emailField.errors()[0].message }}</span>
                        }
                    </label>
                </div>

                @if (submitError()) {
                    <p class="submit-error">{{ submitError() }}</p>
                }

                <div class="actions">
                    <s25-ng-button [type]="'primary'" [onClick]="onSubmit" [disabled]="identityForm().submitting()">
                        Continue
                    </s25-ng-button>
                </div>
            </form>
        </div>
    `,
    styles: `
        .identity-panel {
            max-width: 32rem;
            margin: 0 auto;
            padding: 2rem 1rem 0;
            text-align: left;
        }

        .identity-panel h2 {
            text-align: center;
            margin-top: 0;
            margin-bottom: 0.75rem;
            color: var(--color--link-text);
        }

        .intro {
            text-align: center;
            margin-bottom: 1.5rem;
            color: var(--color--text-muted);
        }

        .field-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
        }

        .field--full {
            grid-column: 1 / -1;
        }

        .field-label {
            display: block;
            margin-bottom: 0.35rem;
            font-weight: 600;
        }

        .required {
            color: var(--color--danger);
        }

        .field--error .cn-form__control {
            border-color: var(--color--danger);
        }

        .field-error,
        .submit-error {
            color: var(--color--danger);
            font-size: 0.875rem;
        }

        .submit-error {
            margin: 0.75rem 0 0;
        }

        .actions {
            margin-top: 1.5rem;
            text-align: center;
        }

        @media (max-width: 480px) {
            .identity-panel {
                padding-left: 1.25rem;
                padding-right: 1.25rem;
            }

            .field-grid {
                grid-template-columns: 1fr;
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, FormField, S25ButtonComponent],
})
export class FormPublicIdentityComponent {
    readonly identitySubmit = output<FormPublicIdentityPayload>();

    readonly submitError = signal<string | undefined>(undefined);

    readonly identityModel = linkedSignal<FormPublicIdentityModel>(() => ({
        firstName: "",
        familyName: "",
        email: "",
    }));

    readonly identityForm = form(this.identityModel, (schema) => {
        required(schema.firstName, { message: "First name is required" });
        required(schema.familyName, { message: "Last name is required" });
        required(schema.email, { message: "Email is required" });
        email(schema.email, { message: "Please enter a valid email address" });
        DBContact.Validators.firstName(schema.firstName);
        DBContact.Validators.familyName(schema.familyName);
        disabled(schema.firstName, ({ stateOf }) => stateOf(schema).submitting());
        disabled(schema.familyName, ({ stateOf }) => stateOf(schema).submitting());
        disabled(schema.email, ({ stateOf }) => stateOf(schema).submitting());
    });

    readonly submitAttempted = signal(false);

    showFieldErrors(field: { invalid: () => boolean; touched: () => boolean }): boolean {
        return (field.touched() || this.submitAttempted()) && field.invalid();
    }

    onSubmit = () => {
        this.submitAttempted.set(true);
        const formState = this.identityForm();
        if (formState.invalid() || formState.submitting() || !formState.dirty()) {
            return;
        }

        submit(this.identityForm, async (identityForm) => {
            const { firstName, familyName, email } = identityForm().value();
            this.submitError.set(undefined);
            this.identitySubmit.emit({
                firstName: firstName.trim(),
                familyName: familyName.trim(),
                email: email.trim(),
            });
        });
    };

    setSubmitError(message: string | undefined) {
        this.submitError.set(message);
    }
}
